import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import useMobile from "../../hooks/useMobile";
import { Link, useLocation } from "react-router-dom";
import DeletePopUp from "../DeletePopUp";
import { useLoader } from "../../context/LoaderContext";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { darkenColor, formatDateBySetting } from "../../exports/functions";
import { IUserDetails } from "../../exports/interfaces";
import { Eye, Pencil } from "lucide-react";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { usePermission } from "../../hooks/usePermission";
import { useOption } from "../../context/OptionContext";
import TableFooter from "../TableFooter";
import { useSettings } from "../../context/SettingsContext";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

export const getTRoleClass = (type: string) => {
  switch (type) {
    case "Complaint Officer":
      return "pill-primary";
    case "Registration Intern":
      return "pill-danger";
    case "Notice Admin":
      return "bg-warning text-dark";
    case "Registeration Officer":
      return "pill-info";
    case "Super Admin":
      return "pill-success";
    default:
      return "bg-secondary";
  }
};

export const getStatusClass = (status: string) => {
  switch (status) {
    case "Active":
      return "pill-success";
    case "Inactive":
      return "pill-danger";
    case "Deactivated":
      return "bg-secondary";
    default:
      return "bg-secondary";
  }
};

const UserTable = ({
  userData,
  setUserData,
  search,
}: {
  userData: IUserDetails[];
  setUserData: Dispatch<SetStateAction<IUserDetails[]>>;
  search: string;
}) => {
  const { setLoading } = useLoader();
  const { statusOptions } = useOption();
  const { generalSettings } = useSettings();
  const { canEdit, canDelete } = usePermission();
  const { user } = useUser();
  const { sendNotification } = useNotification();
  const isMobile = useMobile();
  const location = useLocation();
  const [selectedId, setSelectedId] = useState<number | string | null>(
    location.state?.selectedId ?? null,
  );

  useEffect(() => {
    if (location.state?.selectedId) {
      setSelectedId(location.state.selectedId);
      window.history.replaceState({ ...window.history.state, usr: null }, "");
    }
  }, [location.state?.selectedId]);

  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  const filteredUsers = (userData || []).filter((item) => {
    if (
      selectedId &&
      item.id !== selectedId &&
      item.id !== Number(selectedId)
    ) {
      return false;
    }

    const keyword = (search || "").toLowerCase();

    return (
      item.userName?.toLowerCase().includes(keyword) ||
      item.email?.toLowerCase().includes(keyword) ||
      item.userRole?.toLowerCase().includes(keyword)
    );
  });

  const itemsPerPage = showAll ? Math.max(filteredUsers.length, 1) : 25;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const currentData = showAll
    ? filteredUsers
    : filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const handleClose = () => {
    setShowDelete(false);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleDelete = async () => {
    setLoading(true);

    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/users/${deleteId}`);

      if (response.status === 200 || response.status === 201) {
        setUserData((prev) => prev.filter((item) => item.id !== deleteId));

        setShowDelete(false);
        setDeleteId(null);
        sendNotification(`User deleted`, user?.user?.userName || "User");
        setTimeout(() => {
          setLoading(false);
          dynamicSuccess("User deleted successfully!");
        }, 1000);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Something went wrong!";
      dynamicFailed(message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "User Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  return (
    <>
      <div id="user-table" className="dashboard-card">
        <div className="card-body">
          <h6 className="mb-3">USER MANAGEMENT</h6>

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th className="table-xs">S. no.</th>
                  <th className="table-md">User Name</th>
                  <th className="table-md">Email Address</th>
                  <th className="table-md">Role</th>
                  <th className="table-sm">Status</th>
                  <th className="table-sm">Last Login</th>
                  <th className="table-sm">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentData.length > 0 ? (
                  currentData.map((item, index) => (
                    <tr key={index}>
                      <td
                        className="table-xs text-danger"
                        title={String(indexOfFirstItem + index + 1)}
                      >
                        {String(indexOfFirstItem + index + 1).padStart(2, "0")}
                      </td>

                      <td className="table-md table-bold" title={item.userName}>
                        {item.userName}
                      </td>

                      <td className="table-md" title={item.email}>
                        {item.email}
                      </td>

                      <td className="table-md">
                        <span
                          className={`badge rounded-pill px-3 py-2 mw-100 ${getTRoleClass(item.userRole)}`}
                          title={item.userRole}
                        >
                          {item.userRole}
                        </span>
                      </td>

                      <td className="table-sm">
                        <span
                          className="badge rounded-pill px-3 py-2 mw-100"
                          style={statusStyle(item.userStatus)}
                          title={item.userStatus}
                        >
                          {item.userStatus}
                        </span>
                      </td>

                      <td
                        className="table-sm"
                        title={
                          item.lastLogin
                            ? formatDateBySetting(
                                item.lastLogin,
                                generalSettings?.dateFormat,
                              )
                            : "-"
                        }
                      >
                        {item.lastLogin
                          ? formatDateBySetting(
                              item.lastLogin,
                              generalSettings?.dateFormat,
                            )
                          : "-"}
                      </td>

                      <td className="table-sm">
                        <div className="d-flex gap-3 align-items-center">
                          <Link to={`/user/view/${item.id}`}>
                            <i
                              className="cursor-pointer"
                              data-toggle="tooltip"
                              data-placement="top"
                              title="View"
                            >
                              <Eye />
                            </i>
                          </Link>
                          <Link
                            to={
                              canEdit("Users") ? `/user/edit/${item.id}` : "#"
                            }
                            onClick={(e) => {
                              if (!canEdit("Users")) {
                                e.preventDefault();
                              }
                            }}
                            style={{
                              pointerEvents: canEdit("Users") ? "auto" : "none",
                              opacity: canEdit("Users") ? 1 : 0.5,
                            }}
                          >
                            <i
                              data-toggle="tooltip"
                              data-placement="top"
                              title={
                                canEdit("Users") ? "Edit" : "No permission"
                              }
                            >
                              <Pencil size={20} />
                            </i>
                          </Link>
                          <i
                            className={`fa-solid fa-trash text-danger ${
                              canDelete("Users")
                                ? "cursor-pointer"
                                : "opacity-50"
                            }`}
                            data-toggle="tooltip"
                            data-placement="top"
                            title={
                              canDelete("Users") ? "Delete" : "No permission"
                            }
                            style={{
                              pointerEvents: canDelete("Users")
                                ? "auto"
                                : "none",
                            }}
                            onClick={() => {
                              if (!canDelete("Users")) return;

                              setDeleteId(item.id || 0);
                              setShowDelete(true);
                            }}
                          ></i>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-4 text-muted">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <TableFooter
          isMobile={isMobile}
          totalItems={filteredUsers.length}
          label="Users"
          itemsPerPage={25}
          currentPage={currentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          showAll={showAll}
          onToggleShowAll={() => {
            setShowAll((prev) => !prev);
            setCurrentPage(1);
          }}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default UserTable;
